<template>
  <div>
    <button
      type="button"
      class="btn btn-link p-0 d-inline-flex align-items-center gap-2 text-decoration-none profile-toggle"
      :aria-expanded="open"
      :aria-controls="bodyId"
      @click="open = !open"
    >
      <FontAwesomeIcon :icon="open ? 'chevron-down' : 'chevron-right'" />
      <span class="fw-semibold">{{
        $t("forwards.deals.plan.profile.title")
      }}</span>
    </button>
    <div v-if="open" :id="bodyId" class="mt-1">
      <p class="text-body-tertiary mb-1">
        {{
          $t("forwards.deals.plan.profile.judged_at", {
            date: formatDate(contents.qualification_date),
          })
        }}
      </p>
      <dl class="contents-list mb-0">
        <template v-for="line in lines" :key="line.key">
          <dt>{{ line.title }}</dt>
          <dd :data-breakdown="line.key">
            <template v-if="line.entries.length > 0">
              <span
                v-for="(entry, index) in line.entries"
                :key="entry.label"
                :title="
                  $t('forwards.common.mwh_value', {
                    value: formatNumberWithSpaces(entry.mwh),
                  })
                "
                ><template v-if="index > 0"> · </template>{{ entry.label
                }}{{ " "
                }}<span class="text-body-tertiary">{{
                  percentOf(entry.mwh)
                }}</span></span
              >
            </template>
            <span v-else class="text-body-tertiary">–</span>
          </dd>
        </template>
      </dl>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate, formatNumberWithSpaces } from "@/composables/formatUtils";
import type { DeliveryContents } from "@/forwards2/forwardsTypes";
import { breakdownGroups } from "@/forwards2/picker/productionBreakdown";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";

/** The volume picker's summary groups a delivery's profile shows after its production periods; RE100 is left out. */
const PROFILE_GROUPS = ["country", "technology", "cod", "ecolabels", "support"];

/**
 * A planned delivery's profile: its production periods and the volume picker's summary groups, one
 * line each so a card per delivery stays small, every entry a share of the planned volume. A
 * share's MWh are in its tooltip; an ecolabel no backing mandate holds is left out. Collapsed by
 * default so a plan of many deliveries stays scannable.
 */
const props = defineProps<{
  contents: DeliveryContents;
}>();

const { t } = useI18n();

const bodyId = useId();
const open = ref(false);

const lines = computed(() => [
  {
    key: "vintage",
    title: t("forwards.picker.summary.by_period"),
    entries: props.contents.by_period.map(({ period_label, mwh }) => ({
      label: period_label,
      mwh,
    })),
  },
  ...breakdownGroups(props.contents.mandates, props.contents.unbacked_mwh, t)
    .filter((group) => PROFILE_GROUPS.includes(group.key))
    .map((group) => ({
      key: group.key,
      title: group.title,
      entries: group.entries.filter((entry) => entry.mwh > 0),
    })),
]);

function percentOf(mwh: number): string {
  const total = props.contents.planned_mwh;
  return total > 0 ? `${Math.round((mwh / total) * 100)}%` : "–";
}
</script>

<style scoped lang="scss">
// The theme sets buttons at 0.8rem; the toggle reads as part of the card's text.
.profile-toggle {
  font-size: inherit;
}

.contents-list {
  display: grid;
  grid-template-columns: minmax(7rem, auto) 1fr;
  gap: 0.15rem 0.75rem;

  dt {
    font-weight: 600;
    color: var(--phoenix-gray-600);
  }

  dd {
    margin-bottom: 0;
  }
}
</style>
